'use client';

import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/nextjs';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Loader2Icon,
  Upload as UploadIcon,
  LibraryBig as LibraryBigIcon,
  X as XIcon,
  Music as MusicIcon,
  Video as VideoIcon,
} from 'lucide-react';
import NextImage from 'next/image';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { useProject } from '@/providers/project';
import { uploadFile } from '@/lib/upload';
import { handleError } from '@/lib/error/handle';
import { requestLibraryPick, type LibraryPickResult } from '@/components/canvas/utils/asset-folders-events';
import { SortableGridItem } from './sortable-grid-item';
import {
  type InlineAttachment,
  type InlineMediaConfig,
  detectMediaType,
  parseBytes,
  canAddMore,
  buildAcceptFromConfig,
  getMediaDuration,
  validateMediaItem,
} from '@flowmuse/models/inline-attachments';

const PICKER_MENU_CN =
  'fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-xl min-w-[160px] p-1 z-[200]';
const PICKER_ITEM_CN =
  'rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-medium';

const DEFAULT_MIME: Record<'image' | 'video' | 'audio', string> = {
  image: 'image/png',
  video: 'video/mp4',
  audio: 'audio/mpeg',
};

/** Pure thumbnail renderer (module-level so it is not recreated each render). */
function renderThumb(fileType: InlineAttachment['fileType'], url?: string) {
  if (fileType === 'image' && url) {
    return <NextImage src={url} alt="" fill unoptimized className="object-cover" />;
  }
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted/40">
      {fileType === 'video' ? (
        <VideoIcon size={14} className="text-muted-foreground" />
      ) : (
        <MusicIcon size={14} className="text-muted-foreground" />
      )}
    </div>
  );
}

export type InlineConnectedItem = {
  /** Upstream source node id */
  id: string;
  url?: string;
  label?: string;
  mediaType: 'image' | 'video' | 'audio';
};

export type InlineMediaUploaderProps = {
  config: InlineMediaConfig;
  attachments: InlineAttachment[];
  onChange: (next: InlineAttachment[]) => void;
  /**
   * Optional: return the freshest attachments at merge time. Used after an async
   * upload completes to avoid clobbering concurrent edits with a stale snapshot
   * (mirrors the image/video nodes' `getNode(id)` re-read). Falls back to the
   * `attachments` prop when omitted.
   */
  getLatestAttachments?: () => InlineAttachment[];
  /** Upstream connected media shown as read-only thumbnails (same as image/video nodes) */
  connectedItems?: InlineConnectedItem[];
  onFocusConnected?: (sourceId: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Reusable inline media upload grid, mirroring the image/video node behaviour:
 * thumbnails grid (upstream read-only + inline removable) plus a "+" button with
 * a dropdown to upload from disk or pick from the asset library.
 */
export const InlineMediaUploader = memo(function InlineMediaUploader({
  config,
  attachments,
  onChange,
  getLatestAttachments,
  connectedItems = [],
  onFocusConnected,
  disabled = false,
  className,
}: InlineMediaUploaderProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const { getToken } = useAuth();
  const project = useProject();

  const inlineUploadRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Which media types this config allows (image/video/audio).
  const allowedTypes = useMemo(
    () =>
      (['image', 'video', 'audio'] as const).filter((k) => config?.[k]?.allow),
    [config],
  );

  // Read-only upstream items filtered to allowed types + de-duplicated by source id.
  const connected = useMemo(() => {
    const seen = new Set<string>();
    const out: InlineConnectedItem[] = [];
    for (const it of connectedItems) {
      if (!allowedTypes.includes(it.mediaType)) continue;
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      out.push(it);
    }
    return out;
  }, [connectedItems, allowedTypes]);

  const connectedCounts = useMemo(() => {
    const counts: { image: number; video: number; audio: number } = { image: 0, video: 0, audio: 0 };
    for (const it of connected) counts[it.mediaType]++;
    return counts;
  }, [connected]);

  const pickerFileType = allowedTypes.length === 1 ? allowedTypes[0] : undefined;

  const connectedTotal = connectedCounts.image + connectedCounts.video + connectedCounts.audio;

  // Shared capacity guard for both upload and library-pick paths.
  // Returns a localized error string, or null when the item fits.
  const capacityError = useCallback(
    (fileType: InlineAttachment['fileType'], totalCount: number, typeCount: number): string | null => {
      const typeCfg = config[fileType];
      if (!typeCfg?.allow) return t('This model does not support inline upload');
      if (config.maxTotalCount != null && totalCount >= config.maxTotalCount) {
        return t('Maximum {{count}} files allowed in total', { count: config.maxTotalCount }) as string;
      }
      if (typeCount >= (typeCfg.maxCount ?? Infinity)) {
        return t('Maximum {{count}} {{type}} allowed', { count: typeCfg.maxCount, type: t(fileType) }) as string;
      }
      return null;
    },
    [config, t],
  );

  const handleUploadFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || !user?.id || !project?.id) return;
      setUploading(true);
      try {
        const token = await getToken({ template: 'supabase' });
        const cur = attachments.slice();
        const added: InlineAttachment[] = [];

        for (const file of Array.from(files)) {
          const fileType = detectMediaType(file.type);
          const typeCfg = config[fileType];

          const totalCount = connectedTotal + cur.length + added.length;
          const typeCount =
            (connectedCounts[fileType] ?? 0) +
            cur.filter((a) => a.fileType === fileType).length +
            added.filter((a) => a.fileType === fileType).length;
          const capErr = capacityError(fileType, totalCount, typeCount);
          if (capErr) {
            toast.error(capErr);
            continue;
          }

          const tc = typeCfg!;
          if (tc.formats?.length) {
            const mime = file.type.toLowerCase();
            const ok = tc.formats.some(
              (f) => f === mime || (f.endsWith('/*') && mime.startsWith(f.slice(0, -1))),
            );
            if (!ok) {
              toast.error(t('Unsupported format {{format}}', { format: file.type || file.name.split('.').pop() }));
              continue;
            }
          }

          if (file.size > parseBytes(tc.maxSize)) {
            toast.error(t('File exceeds size limit of {{size}}', { size: tc.maxSize }));
            continue;
          }

          // Duration constraints (video/audio)
          let duration: number | undefined;
          if (fileType !== 'image') {
            duration = await getMediaDuration(file);
            const err = validateMediaItem(tc, config, fileType, { duration }, 0);
            if (err) {
              toast.error(t(err.key, err.params) as string);
              continue;
            }
          }

          const { url, type } = await uploadFile(file, 'files', {
            token: token ?? '',
            userId: user.id,
            projectId: String(project.id),
            orgId: (project as any)?.orgId || undefined,
            assetType: fileType,
          });
          added.push({ id: nanoid(), url, type, fileType, name: file.name, duration });
        }

        if (added.length > 0) {
          // Re-read the freshest list so concurrent edits during the upload are not lost.
          const base = getLatestAttachments ? getLatestAttachments() : cur;
          onChange([...base, ...added]);
        }
      } catch (error) {
        handleError(t('Upload failed'), error);
      } finally {
        setUploading(false);
        if (inlineUploadRef.current) inlineUploadRef.current.value = '';
      }
    },
    [user?.id, project, getToken, attachments, config, connectedTotal, connectedCounts, capacityError, getLatestAttachments, onChange, t],
  );

  const handlePickFromLibrary = useCallback(
    (item: LibraryPickResult) => {
      const fileType: InlineAttachment['fileType'] =
        item.type === 'video' || item.type === 'audio' ? item.type : 'image';
      const cur = attachments.slice();
      const totalCount = connectedTotal + cur.length;
      const typeCount =
        (connectedCounts[fileType] ?? 0) + cur.filter((a) => a.fileType === fileType).length;
      const capErr = capacityError(fileType, totalCount, typeCount);
      if (capErr) {
        toast.error(capErr);
        return;
      }
      onChange([
        ...cur,
        {
          id: nanoid(),
          url: item.rawSrc ?? item.src,
          type: item.contentType || DEFAULT_MIME[fileType],
          fileType,
          name: item.name,
        },
      ]);
    },
    [attachments, connectedTotal, connectedCounts, capacityError, onChange],
  );

  const openLibraryPicker = useCallback(() => {
    void requestLibraryPick({ fileType: pickerFileType }).then((item) => {
      if (item) handlePickFromLibrary(item);
    });
  }, [pickerFileType, handlePickFromLibrary]);

  const handleRemove = useCallback(
    (attId: string) => {
      onChange(attachments.filter((a) => a.id !== attId));
    },
    [attachments, onChange],
  );

  const handleReorder = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = attachments.findIndex((a) => a.id === active.id);
      const newIdx = attachments.findIndex((a) => a.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return;
      onChange(arrayMove(attachments, oldIdx, newIdx));
    },
    [attachments, onChange],
  );

  if (!config?.allowInline) return null;

  const showUploadBtn = !disabled && canAddMore(config, attachments, connectedCounts);

  if (connected.length === 0 && attachments.length === 0 && !showUploadBtn) return null;

  return (
    <div className={cn('w-full overflow-hidden', className)}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
        <SortableContext items={attachments.map((a) => a.id)} strategy={rectSortingStrategy}>
          <div className="grid w-full grid-cols-8 gap-1.5">
            {connected.map((it) => (
              <button
                key={`upstream-${it.id}`}
                type="button"
                className={cn(
                  'relative w-full aspect-square overflow-hidden rounded-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                  it.url ? 'bg-muted/30' : 'bg-muted/30 border border-dashed border-muted-foreground/20',
                )}
                title={it.label}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFocusConnected?.(it.id);
                }}
              >
                {it.url ? (
                  renderThumb(it.mediaType, it.url)
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-0.5">
                    {it.mediaType === 'video' ? (
                      <VideoIcon size={12} className="text-muted-foreground/40" />
                    ) : (
                      <MusicIcon size={12} className="text-muted-foreground/40" />
                    )}
                    <span className="text-[7px] leading-tight text-muted-foreground/40">{t('Pending')}</span>
                  </div>
                )}
              </button>
            ))}

            {attachments.map((att) => (
              <SortableGridItem key={`inline-${att.id}`} id={att.id} disabled={disabled}>
                <div
                  className="relative w-full aspect-square bg-muted/30 overflow-hidden group rounded-lg"
                  title={att.name || att.fileType}
                >
                  {renderThumb(att.fileType, att.previewUrl ?? att.url)}
                  {!disabled && (
                    <button
                      type="button"
                      className="absolute top-0 left-0 h-5 w-5 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(att.id);
                      }}
                    >
                      <XIcon size={10} />
                    </button>
                  )}
                </div>
              </SortableGridItem>
            ))}

            {showUploadBtn && !uploading && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/30 transition-colors"
                    title={t('Upload media inline')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                  <DropdownMenuItem className={PICKER_ITEM_CN} onClick={() => inlineUploadRef.current?.click()}>
                    <UploadIcon size={14} className="mr-1.5" />
                    {t('Upload media inline')}
                  </DropdownMenuItem>
                  <DropdownMenuItem className={PICKER_ITEM_CN} onClick={openLibraryPicker}>
                    <LibraryBigIcon size={14} className="mr-1.5" />
                    {t('Pick from library')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {showUploadBtn && uploading && (
              <div className="relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
                <Loader2Icon size={13} className="animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>

      <input
        ref={inlineUploadRef}
        type="file"
        accept={buildAcceptFromConfig(config)}
        multiple
        className="hidden"
        onChange={(e) => void handleUploadFiles(e.target.files)}
      />

    </div>
  );
});
